import { useState } from 'react';
import BaseButton from 'components/ui/BaseButton';
import FloatingLabelInput from 'components/ui/FloatingLabelInput';
import { Add, TrashOutlined } from 'assets/icons/revamp';
import COLOR from 'constants/color';
import { t } from 'services/i18n.service';
import { isValidActivityChoiceVideoUrl } from 'utils/activity-choices.util';

interface YouTubeVideosProps {
  videoUrls: string[];
  onVideoUrlsChange: (urls: string[]) => void;
}

const YouTubeVideos: React.FC<YouTubeVideosProps> = ({
  videoUrls,
  onVideoUrlsChange
}) => {
  const [videoUrlInput, setVideoUrlInput] = useState('');
  const trimmedVideoUrlInput = videoUrlInput.trim();
  const isVideoUrlInputDuplicate = videoUrls.some(
    (url) => url.trim() === trimmedVideoUrlInput
  );
  const isVideoUrlInputInvalid =
    Boolean(trimmedVideoUrlInput) &&
    !isValidActivityChoiceVideoUrl(trimmedVideoUrlInput);

  const getVideoUrlInputError = () => {
    if (isVideoUrlInputInvalid) {
      return t('validation.invalid_url');
    }
    if (isVideoUrlInputDuplicate) {
      return t('errors.duplicate_url');
    }
    return '';
  };

  const videoUrlInputError = getVideoUrlInputError();

  const handleAddVideoUrl = () => {
    if (
      trimmedVideoUrlInput &&
      !isVideoUrlInputInvalid &&
      !isVideoUrlInputDuplicate
    ) {
      const newUrl = trimmedVideoUrlInput;
      const currentUrls = videoUrls || [];

      onVideoUrlsChange([...currentUrls, newUrl]);
      setVideoUrlInput('');
    }
  };

  const handleDeleteVideoUrl = (index: number) => {
    const newUrls = videoUrls.filter((_, i) => i !== index);
    onVideoUrlsChange(newUrls);
  };

  return (
    <div className='w-full lg:w-1/2 flex flex-col border border-gray-300 dark:border-gray-600 rounded-md p-2 ~gap-2/3'>
      <div className='flex items-center justify-between border-b px-1 py-0.5'>
        <p className='~text-xs/sm'>{t('youtube_videos')}</p>
        <BaseButton
          title={t('tabs_revamp.random_choice_form.add_url')}
          onClick={handleAddVideoUrl}
          disabled={!trimmedVideoUrlInput || Boolean(videoUrlInputError)}
          size='xs'
          variant='secondary'
          icon={<Add styles='~w-3/4 ~h-3/4' fill={COLOR.PRIMARY.LIGHT_900} />}
        />
      </div>

      <div className='flex flex-col ~gap-2/3'>
        <FloatingLabelInput
          type='text'
          placeholder={t(
            'tabs_revamp.random_choice_form.enter_youtube_video_url'
          )}
          value={videoUrlInput}
          onChange={(e) => setVideoUrlInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleAddVideoUrl();
            }
          }}
          size='small'
          showLabel={true}
          label=''
          error={videoUrlInputError}
          inputClassName='!text-xs'
        />
      </div>

      <div className='flex-1 flex flex-col ~gap-2/3 ~min-h-32 max-h-64 overflow-y-auto'>
        {!videoUrls || videoUrls.length === 0 ? (
          <p className='~text-[0.625rem]/xs text-primary-900 dark:text-blue-400 text-center my-auto'>
            {t('no_youtube_urls_found')}
          </p>
        ) : (
          <div className='w-full flex flex-col ~gap-2/3'>
            {videoUrls.map((url, index) => {
              const isVideoUrlDuplicate =
                videoUrls.filter((videoUrl) => videoUrl.trim() === url.trim())
                  .length > 1;
              const isVideoUrlValid =
                isValidActivityChoiceVideoUrl(url) && !isVideoUrlDuplicate;
              return (
                <div
                  key={index}
                  className={`flex items-center justify-between p-2 border rounded ${
                    isVideoUrlValid
                      ? 'border-gray-200 dark:border-gray-700'
                      : 'border-red-400 dark:border-red-500 bg-red-50/50 dark:bg-red-900/10'
                  }`}>
                  <p
                    className={`~text-xs/sm flex-1 truncate ${
                      isVideoUrlValid
                        ? 'text-gray-600 dark:text-gray-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}>
                    {url}
                  </p>
                  <button
                    onClick={() => handleDeleteVideoUrl(index)}
                    className='flex-shrink-0 ~p-1/1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors'
                    aria-label={t('tabs_revamp.videos_tab.delete_video')}>
                    <TrashOutlined
                      className='~w-3/5 ~h-3/5'
                      fill={COLOR.SEMANTIC.ERROR}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default YouTubeVideos;
